// Pure helpers behind the phone-menu guide's "live" status: which menus and
// departments are open right now, when that changes next, which key presses
// reach a department, and how many of its agents are available. No React, no
// network — everything here is unit-tested with an injected `now`.

import type {
  CallFlow, CallFlowNode, AutoAttendantNode, QueueAgent, WeeklyHours, Weekday, TimeInterval, HolidayRange,
} from '../../../api/client';

export const WEEKDAY_KEYS: Weekday[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// Teams stores Windows time-zone ids; Intl needs IANA names. The common ones —
// anything unknown yields "status unknown" rather than a wrong answer.
const WINDOWS_TO_IANA: Record<string, string> = {
  'W. Europe Standard Time': 'Europe/Amsterdam',
  'Romance Standard Time': 'Europe/Paris',
  'Central Europe Standard Time': 'Europe/Warsaw',
  'Central European Standard Time': 'Europe/Belgrade',
  'GMT Standard Time': 'Europe/London',
  'Greenwich Standard Time': 'Atlantic/Reykjavik',
  'E. Europe Standard Time': 'Europe/Chisinau',
  'FLE Standard Time': 'Europe/Kiev',
  'GTB Standard Time': 'Europe/Bucharest',
  'Russian Standard Time': 'Europe/Moscow',
  'Turkey Standard Time': 'Europe/Istanbul',
  'Israel Standard Time': 'Asia/Jerusalem',
  'Arabian Standard Time': 'Asia/Dubai',
  'Arab Standard Time': 'Asia/Riyadh',
  'India Standard Time': 'Asia/Kolkata',
  'China Standard Time': 'Asia/Shanghai',
  'Singapore Standard Time': 'Asia/Singapore',
  'Tokyo Standard Time': 'Asia/Tokyo',
  'Korea Standard Time': 'Asia/Seoul',
  'AUS Eastern Standard Time': 'Australia/Sydney',
  'E. Australia Standard Time': 'Australia/Brisbane',
  'AUS Central Standard Time': 'Australia/Darwin',
  'W. Australia Standard Time': 'Australia/Perth',
  'New Zealand Standard Time': 'Pacific/Auckland',
  'Eastern Standard Time': 'America/New_York',
  'Central Standard Time': 'America/Chicago',
  'Mountain Standard Time': 'America/Denver',
  'US Mountain Standard Time': 'America/Phoenix',
  'Pacific Standard Time': 'America/Los_Angeles',
  'Alaskan Standard Time': 'America/Anchorage',
  'Hawaiian Standard Time': 'Pacific/Honolulu',
  'Atlantic Standard Time': 'America/Halifax',
  'SA Pacific Standard Time': 'America/Bogota',
  'E. South America Standard Time': 'America/Sao_Paulo',
  'Argentina Standard Time': 'America/Buenos_Aires',
  'Central America Standard Time': 'America/Guatemala',
  'Canada Central Standard Time': 'America/Regina',
  'South Africa Standard Time': 'Africa/Johannesburg',
  'Egypt Standard Time': 'Africa/Cairo',
  'W. Central Africa Standard Time': 'Africa/Lagos',
  'Morocco Standard Time': 'Africa/Casablanca',
  'UTC': 'UTC',
  'Coordinated Universal Time': 'UTC',
};

/** IANA zone for a Windows id (or an IANA id passed through); null if unknown. */
export function toIana(tz?: string): string | null {
  if (!tz) return null;
  const t = tz.trim();
  if (WINDOWS_TO_IANA[t]) return WINDOWS_TO_IANA[t];
  if (t === 'UTC' || /^[A-Za-z_]+\/[A-Za-z_+\-0-9]+(\/[A-Za-z_+\-0-9]+)?$/.test(t)) {
    try { new Intl.DateTimeFormat('en', { timeZone: t }); return t; } catch { return null; }
  }
  return null;
}

export interface LocalParts { weekday: Weekday; minutes: number; ymd: string; hhmm: string }

const fmtCache = new Map<string, Intl.DateTimeFormat>();
function fmt(iana: string): Intl.DateTimeFormat {
  let f = fmtCache.get(iana);
  if (!f) {
    f = new Intl.DateTimeFormat('en-GB', {
      timeZone: iana, weekday: 'short', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
    fmtCache.set(iana, f);
  }
  return f;
}

/** Local wall-clock parts of an instant in a zone. */
export function localParts(now: Date, iana: string): LocalParts {
  const parts = fmt(iana).formatToParts(now);
  const get = (type: string) => parts.find(p => p.type === type)?.value || '';
  const weekday = get('weekday').slice(0, 3).toLowerCase() as Weekday;
  let hour = Number(get('hour'));
  if (hour === 24) hour = 0;
  const minute = Number(get('minute'));
  const ymd = `${get('year')}-${get('month')}-${get('day')}`;
  return { weekday, minutes: hour * 60 + minute, ymd, hhmm: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}` };
}

/** The instant at which a zone's wall clock reads `ymd`T`hhmm` (DST-aware; on a
 *  non-existent local time the later interpretation is used). */
export function zonedToUtc(ymd: string, hhmm: string, iana: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  const [h, mi] = hhmm.split(':').map(Number);
  const wanted = Date.UTC(y, m - 1, d, h, mi);
  let guess = wanted;
  for (let i = 0; i < 2; i++) {
    const p = localParts(new Date(guess), iana);
    const [py, pm, pd] = p.ymd.split('-').map(Number);
    const asUtc = Date.UTC(py, pm - 1, pd, Math.floor(p.minutes / 60), p.minutes % 60);
    const diff = asUtc - wanted;
    if (diff === 0) break;
    guess -= diff;
  }
  return new Date(guess);
}

function holidayBounds(h: HolidayRange): { start: string; end: string; endInclusive: boolean } {
  const start = h.start.length === 10 ? `${h.start}T00:00` : h.start;
  const endInclusive = h.end.length === 10;
  const end = endInclusive ? `${h.end}T23:59` : h.end;
  return { start, end, endInclusive };
}

function isHolidayAt(holidays: HolidayRange[] | undefined, p: LocalParts): boolean {
  if (!holidays?.length) return false;
  const stamp = `${p.ymd}T${p.hhmm}`;
  return holidays.some(h => {
    const b = holidayBounds(h);
    return stamp >= b.start && (b.endInclusive ? stamp <= b.end : stamp < b.end);
  });
}

export type OpenState = 'open' | 'closed' | 'unknown';

/** Open/closed at an instant: holidays win, then the weekday's periods. */
export function isOpenAt(weekly: WeeklyHours | undefined, holidays: HolidayRange[] | undefined, iana: string | null, now: Date): OpenState {
  if (!weekly || !iana) return 'unknown';
  const p = localParts(now, iana);
  if (isHolidayAt(holidays, p)) return 'closed';
  const intervals = weekly[p.weekday] || [];
  return intervals.some(([s, e]) => p.minutes >= s && p.minutes < e) ? 'open' : 'closed';
}

function addDays(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`;
}

function weekdayOf(ymd: string): Weekday {
  const [y, m, d] = ymd.split('-').map(Number);
  return (['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as Weekday[])[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
}

function minutesToHHMM(min: number): string {
  const h = Math.floor(min / 60); const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** The next moment the open/closed state flips, scanning up to `maxDays`. */
export function nextChange(
  weekly: WeeklyHours | undefined, holidays: HolidayRange[] | undefined, iana: string | null, now: Date, maxDays = 14,
): { at: Date; to: 'open' | 'closed' } | null {
  if (!weekly || !iana) return null;
  const current = isOpenAt(weekly, holidays, iana, now);
  if (current === 'unknown') return null;
  const today = localParts(now, iana).ymd;
  const candidates: Date[] = [];
  for (let d = 0; d <= maxDays; d++) {
    const ymd = addDays(today, d);
    for (const [s, e] of weekly[weekdayOf(ymd)] || []) {
      candidates.push(zonedToUtc(ymd, minutesToHHMM(s), iana));
      if (e >= 1440) candidates.push(zonedToUtc(addDays(ymd, 1), '00:00', iana));
      else candidates.push(zonedToUtc(ymd, minutesToHHMM(e), iana));
    }
  }
  for (const h of holidays || []) {
    const b = holidayBounds(h);
    candidates.push(zonedToUtc(b.start.slice(0, 10), b.start.slice(11, 16), iana));
    const endStamp = b.endInclusive ? `${addDays(h.end, 1)}T00:00` : b.end;
    candidates.push(zonedToUtc(endStamp.slice(0, 10), endStamp.slice(11, 16), iana));
  }
  const horizon = now.getTime() + (maxDays + 1) * 86400000;
  const sorted = candidates
    .filter(c => c.getTime() > now.getTime() && c.getTime() <= horizon)
    .sort((a, b) => a.getTime() - b.getTime());
  for (const at of sorted) {
    const state = isOpenAt(weekly, holidays, iana, at);
    if (state !== 'unknown' && state !== current) return { at, to: state };
  }
  return null;
}

function intersectIntervals(a: TimeInterval[], b: TimeInterval[]): TimeInterval[] {
  const out: TimeInterval[] = [];
  for (const [s1, e1] of a) for (const [s2, e2] of b) {
    const s = Math.max(s1, s2); const e = Math.min(e1, e2);
    if (e > s) out.push([s, e]);
  }
  return out.sort((x, y) => x[0] - y[0]);
}

/** Hours during which BOTH schedules are open (a nested menu is reachable
 *  only while every menu on the way is open). */
export function intersectWeekly(a: WeeklyHours, b: WeeklyHours): WeeklyHours {
  const out: WeeklyHours = {};
  for (const d of WEEKDAY_KEYS) {
    const list = intersectIntervals(a[d] || [], b[d] || []);
    if (list.length) out[d] = list;
  }
  return out;
}

export interface DialStep { fromId: string; key: string; toId: string }
export interface DialPath {
  targetId: string;
  steps: DialStep[];
  /** Auto attendants passed through, root first (node ids). */
  aaIds: string[];
  /** 'nl', 'en', …, or 'both' for the root itself. */
  language: string;
}

/** 'nl-NL' → 'nl'. */
export function languageCode(code?: string): string {
  const m = /^([a-z]{2})/i.exec(String(code || '').trim());
  return m ? m[1].toLowerCase() : '';
}

const MAX_PATHS = 500;
const MAX_DEPTH = 6;

/** Every key-press route from the open-hours entry to each node, cycle-safe.
 *  Menus reachable only outside hours (after-hours targets) are not dial
 *  routes and are skipped. */
export function enumeratePaths(flow: CallFlow): Map<string, DialPath[]> {
  const byId = new Map(flow.nodes.map(n => [n.id, n]));
  const out = new Map<string, DialPath[]>();
  let count = 0;
  const rootCode = (() => { const r = byId.get(flow.openTargetId); return r && r.type === 'autoAttendant' ? languageCode(r.language) : ''; })();

  const record = (targetId: string, steps: DialStep[], aaIds: string[]) => {
    if (count >= MAX_PATHS) return;
    // The languages of the menus a caller passed through (root excluded, the
    // target itself included when it is a menu). The root alone is 'both'.
    const codes = [...aaIds.slice(1), targetId]
      .map(id => { const n = byId.get(id); return n && n.type === 'autoAttendant' && id !== flow.openTargetId ? languageCode(n.language) : ''; })
      .filter(Boolean);
    const language = steps.length === 0 ? 'both' : codes.length ? codes[codes.length - 1] : (rootCode || 'both');
    const list = out.get(targetId) || [];
    list.push({ targetId, steps, aaIds, language });
    out.set(targetId, list);
    count++;
  };

  const walk = (nodeId: string, steps: DialStep[], aaIds: string[], visited: Set<string>) => {
    const node = byId.get(nodeId);
    if (!node || count >= MAX_PATHS) return;
    record(nodeId, steps, aaIds);
    if (node.type !== 'autoAttendant' || steps.length >= MAX_DEPTH) return;
    const nextAa = [...aaIds, nodeId];
    const options: Array<{ key: string; toId: string }> = node.menu.map(m => ({ key: m.key, toId: m.targetId }));
    if (node.operatorTargetId && !node.menu.some(m => m.key === '0')) options.push({ key: '0', toId: node.operatorTargetId });
    for (const o of options) {
      if (visited.has(o.toId)) continue;
      const nextVisited = new Set(visited); nextVisited.add(o.toId);
      walk(o.toId, [...steps, { fromId: nodeId, key: o.key, toId: o.toId }], nextAa, nextVisited);
    }
  };
  walk(flow.openTargetId, [], [], new Set([flow.openTargetId]));
  // Shortest route first for every target.
  for (const list of out.values()) list.sort((a, b) => a.steps.length - b.steps.length);
  return out;
}

export interface EffectiveHours { weekly?: WeeklyHours; holidays: HolidayRange[]; iana: string | null; inherited: boolean }

/** Hours a target is reachable through a given route: the location's hours
 *  intersected with every nested menu's own hours on the way. */
export function effectiveHours(flow: CallFlow, path: DialPath | undefined): EffectiveHours {
  const iana = toIana(flow.timezone);
  let weekly: WeeklyHours | undefined = flow.schedule.weekly;
  const holidays: HolidayRange[] = [...(flow.schedule.holidayRanges || [])];
  let inherited = true;
  const ids = path ? [...path.aaIds, path.targetId] : [flow.openTargetId];
  for (const id of ids) {
    const n = flow.nodes.find(x => x.id === id);
    if (!n || n.type !== 'autoAttendant') continue;
    const aa = n as AutoAttendantNode;
    if (aa.hours && Object.keys(aa.hours).length) {
      weekly = weekly ? intersectWeekly(weekly, aa.hours) : aa.hours;
      if (id !== flow.openTargetId) inherited = false;
    }
    for (const h of aa.holidays || []) if (!holidays.some(x => x.name === h.name && x.start === h.start)) holidays.push(h);
  }
  return { weekly, holidays, iana, inherited };
}

export interface PresenceInfo { availability: string; activity?: string }

const AVAILABLE = new Set(['available', 'availableidle']);

/** How many agents of a queue are reachable right now: opted-in agents whose
 *  presence is Available. Agents without a presence reading count as unknown. */
export function queueAvailability(agents: QueueAgent[] | undefined, presence?: Map<string, PresenceInfo>): { total: number; available: number; unknown: number } {
  const list = agents || [];
  let available = 0; let unknown = 0;
  for (const a of list) {
    const p = presence?.get(a.id.toLowerCase());
    if (!p) { unknown++; continue; }
    if (a.optIn !== false && AVAILABLE.has(String(p.availability).toLowerCase())) available++;
  }
  return { total: list.length, available, unknown };
}

/** Headline numbers for the Start page. */
export function guideStats(flow: CallFlow): { menus: number; departments: number; agents: number; groups: number } {
  const aas = flow.nodes.filter(n => n.type === 'autoAttendant') as AutoAttendantNode[];
  const withIdentity = aas.filter(a => a.teamsId);
  const agentIds = new Set<string>();
  for (const n of flow.nodes) if (n.type === 'queue') for (const a of n.agents || []) agentIds.add(a.id.toLowerCase());
  return {
    menus: withIdentity.length || aas.length,
    departments: flow.nodes.filter(n => n.type === 'queue').length,
    agents: agentIds.size,
    groups: flow.groups?.length || 0,
  };
}

/** Compact "Mo–Fr 08:30–17:30 · Sa 09:00–12:00" style summary. */
export function compactHours(weekly: WeeklyHours | undefined, dayShort: (d: Weekday) => string, closedLabel: string): string {
  if (!weekly) return closedLabel;
  const keyOf = (d: Weekday) => JSON.stringify(weekly[d] || []);
  const groups: string[] = [];
  let i = 0;
  while (i < WEEKDAY_KEYS.length) {
    let j = i;
    while (j + 1 < WEEKDAY_KEYS.length && keyOf(WEEKDAY_KEYS[j + 1]) === keyOf(WEEKDAY_KEYS[i])) j++;
    const list = weekly[WEEKDAY_KEYS[i]] || [];
    if (list.length) {
      const label = i === j ? dayShort(WEEKDAY_KEYS[i]) : `${dayShort(WEEKDAY_KEYS[i])}–${dayShort(WEEKDAY_KEYS[j])}`;
      const isAllDay = list.length === 1 && list[0][0] === 0 && list[0][1] >= 1440;
      groups.push(`${label} ${isAllDay ? '00:00–24:00' : list.map(([s, e]) => `${minutesToHHMM(s)}–${e >= 1440 ? '24:00' : minutesToHHMM(e)}`).join(', ')}`);
    }
    i = j + 1;
  }
  return groups.length ? groups.join(' · ') : closedLabel;
}

export function intervalsText(list: TimeInterval[] | undefined, closedLabel: string, alwaysLabel: string): string {
  if (!list || !list.length) return closedLabel;
  if (list.length === 1 && list[0][0] === 0 && list[0][1] >= 1440) return alwaysLabel;
  return list.map(([s, e]) => `${minutesToHHMM(s)}–${e >= 1440 ? '24:00' : minutesToHHMM(e)}`).join(', ');
}

export function isMenuNode(n: CallFlowNode | undefined): n is AutoAttendantNode {
  return !!n && n.type === 'autoAttendant';
}
